import { axiosInstance } from './api';

export interface InviteDto {
  id: number;
  token: string;
  role: string;
  expires_at: string;
  created_by_username: string;
  redeemed_at: string | null;
  /** Persistent invites stay usable until expires_at — the employee-link
   *  pattern (pinned in a branch TG chat, click as many times as needed). */
  is_persistent: boolean;
  /** How many times this invite has been redeemed (incremented on every
   *  use for persistent invites; once-and-only-once for one-shot). */
  redemption_count: number;
  /** '' = generic operator-issued link.
   *  'pin_message' = the per-zone link pinned in the branch TG chat
   *  (auto-renews on access, one-per-zone, reissue-only). */
  purpose: '' | 'pin_message';
  /** TG Mini App deep-link when the bot has a Mini App configured at
   *  BotFather. Null when TELEGRAM_MINIAPP_BOT_NAME env is unset
   *  (UI hides the second copy button in that case). */
  miniapp_url: string | null;
  /** When the bot has successfully pinned this invite in the branch
   *  chat (null if never pinned, or if the pin was invalidated by a
   *  token reissue). Drives the Notifications-tab "currently pinned"
   *  status pill. */
  pinned_in_chat_at: string | null;
}

export interface MemberDto {
  id: number;
  username: string;
  role: string;
  granted_at: string | null;
}

export interface ZoneAccessDto {
  invites: InviteDto[];
  members: MemberDto[];
  /** Highest rank the requester is allowed to grant. Use to disable
   *  role options above the requester's ceiling in role pickers. */
  max_role_rank: number;
}

export interface InviteInfoDto {
  zone_id: number;
  zone_name: string;
  role: string;
  expires_at: string;
  is_persistent?: boolean;
  purpose?: '' | 'pin_message';
}

export const getZoneAccess = (zoneId: string) =>
  axiosInstance.get<ZoneAccessDto>(`/core/api/zones/${zoneId}/invites/`);

export const createInvite = (
  zoneId: string,
  role: string,
  expiresDays = 7,
  isPersistent = false,
) =>
  axiosInstance.post<InviteDto>(
    `/core/api/zones/${zoneId}/invites/`,
    { role, expires_days: expiresDays, is_persistent: isPersistent },
  );

export const revokeInvite = (inviteId: number) =>
  axiosInstance.delete(`/core/api/invites/${inviteId}/`);

export const updateInviteRole = (inviteId: number, role: string) =>
  axiosInstance.patch<InviteDto>(`/core/api/invites/${inviteId}/`, { role });

export const getPinInvite = (zoneId: string) =>
  axiosInstance.get<InviteDto>(`/core/api/zones/${zoneId}/pin-invite/`);

export const reissuePinInvite = (zoneId: string) =>
  axiosInstance.post<InviteDto>(`/core/api/zones/${zoneId}/pin-invite/reissue/`);

export const sendPinToChat = (
  zoneId: string,
  chatIdOverride?: string,
  threadIdOverride?: string,
) =>
  axiosInstance.post<InviteDto>(
    `/core/api/zones/${zoneId}/pin-invite/send-to-chat/`,
    {
      ...(chatIdOverride ? { chat_id_override: chatIdOverride } : {}),
      // Forum topic to post into. Only meaningful alongside a chat override —
      // without one the BE uses the zone's own prod_thread_id.
      ...(threadIdOverride ? { thread_id_override: threadIdOverride } : {}),
    },
  );

export const getPinPreview = (zoneId: string) =>
  axiosInstance.get<{ text: string }>(`/core/api/zones/${zoneId}/pin-invite/preview/`);

export const addMember = (
  zoneId: string,
  payload: { username: string; role: string; password?: string },
) =>
  axiosInstance.post<MemberDto & { created_user?: boolean }>(
    `/core/api/zones/${zoneId}/members/`,
    payload,
  );

export interface UserSearchHit {
  id: number;
  username: string;
  email: string;
}

export const searchUsers = (q: string) =>
  axiosInstance.get<{ results: UserSearchHit[] }>(`/core/api/users/search/`, {
    params: { q },
  });

export const removeMember = (zoneId: string, membershipId: number) =>
  axiosInstance.delete(`/core/api/zones/${zoneId}/members/${membershipId}/`);

export const updateMember = (zoneId: string, membershipId: number, role: string) =>
  axiosInstance.patch<MemberDto>(`/core/api/zones/${zoneId}/members/${membershipId}/`, { role });

export const getInviteInfo = (token: string) =>
  axiosInstance.get<InviteInfoDto>(`/core/api/invites/token/${token}/`);

export const redeemInvite = (token: string, payload: { username?: string; password?: string }) =>
  axiosInstance.post<{ access: string; refresh: string; zone_id: number }>(
    `/core/api/invites/token/${token}/redeem/`,
    payload,
  );

// ── Role helpers ──────────────────────────────────────────────────────────────

/** Rank ladder. Mirror of the BE `_ROLE_ORDER` constant in invites.py.
 *  Used to grey out roles the operator can't grant. */
export const ROLE_RANK: Record<string, number> = {
  zone_employee: 1,
  line_worker: 1,
  chain_office: 2,
  zone_manager: 3,
  zone_admin: 4,
};
